import { Checkbox } from '../../components/Checkbox';
import { Dropdown } from '../../components/Dropdown';
import { FloatingField } from '../../components/FloatingField';
import { FormCard } from '../../components/FormCard';
import { Input } from '../../components/Input';
import {
  CostRow,
  MessageBox,
  MethodHeading,
  MethodLabel,
  MethodOption,
  MethodRow,
  Radio,
  ShippingFormStack,
  SplitRow,
  Wrapper,
} from './DeliveryStep.styles';
import type { DeliveryStepLabels, DeliveryStepProps } from './DeliveryStep.types';

const DEFAULT_LABELS: Required<DeliveryStepLabels> = {
  chooseMethod: 'Choose your delivery method',
  methodPostal: 'Postal delivery',
  methodCollection: 'Click & collect',
  useBillingAsShipping: 'Use billing address as shipping address',
  shippingAddressLabel: 'Address',
  shippingPostalCodeLabel: 'Postcode',
  shippingCountryLabel: 'Country',
  shippingCountryPlaceholder: 'Select',
};

export function DeliveryStep({
  values,
  onMethodChange,
  onUseBillingAsShippingChange,
  onShippingChange,
  isPostalAllowed = true,
  isCollectionAllowed = true,
  canUseBillingAsShipping = true,
  postalCost,
  collectionDetails,
  countryOptions,
  errors,
  labels,
}: DeliveryStepProps) {
  const merged = { ...DEFAULT_LABELS, ...labels };
  const showShippingForm = !canUseBillingAsShipping || !values.useBillingAsShipping;
  const isPostal = values.method === 'post';

  return (
    <Wrapper data-testid="delivery-step">
      <MethodHeading>{merged.chooseMethod}</MethodHeading>

      <MethodRow role="radiogroup" aria-label={merged.chooseMethod}>
        {isPostalAllowed && (
          <MethodOption data-selected={isPostal}>
            <MethodLabel>{merged.methodPostal}</MethodLabel>
            <Radio
              type="radio"
              name="deliveryMethod"
              value="post"
              checked={isPostal}
              onChange={() => onMethodChange('post')}
              aria-label={merged.methodPostal}
            />
          </MethodOption>
        )}
        {isCollectionAllowed && (
          <MethodOption data-selected={!isPostal}>
            <MethodLabel>{merged.methodCollection}</MethodLabel>
            <Radio
              type="radio"
              name="deliveryMethod"
              value="collection"
              checked={!isPostal}
              onChange={() => onMethodChange('collection')}
              aria-label={merged.methodCollection}
            />
          </MethodOption>
        )}
      </MethodRow>

      {isPostal && postalCost && (
        <MessageBox>
          <CostRow>
            <span>{postalCost.label}</span>
            <span>{postalCost.price}</span>
          </CostRow>
        </MessageBox>
      )}
      {!isPostal && collectionDetails && (
        <MessageBox>
          <span>{collectionDetails}</span>
        </MessageBox>
      )}

      {isPostal && canUseBillingAsShipping && (
        <Checkbox
          checked={values.useBillingAsShipping}
          onChange={onUseBillingAsShippingChange}
          label={merged.useBillingAsShipping}
        />
      )}

      {isPostal && showShippingForm && (
        <ShippingFormStack>
          <FormCard>
            <FloatingField
              label={merged.shippingAddressLabel}
              hasValue={Boolean(values.shippingAddress)}
              error={errors?.shippingAddress}
            >
              <Input
                type="text"
                autoComplete="street-address"
                value={values.shippingAddress}
                onChange={(e) => onShippingChange('shippingAddress', e.target.value)}
                error={Boolean(errors?.shippingAddress)}
                placeholder={`${merged.shippingAddressLabel}*`}
                aria-label={merged.shippingAddressLabel}
              />
            </FloatingField>

            <SplitRow>
              <FloatingField
                label={merged.shippingPostalCodeLabel}
                hasValue={Boolean(values.shippingPostalCode)}
                error={errors?.shippingPostalCode}
              >
                <Input
                  type="text"
                  autoComplete="postal-code"
                  value={values.shippingPostalCode}
                  onChange={(e) => onShippingChange('shippingPostalCode', e.target.value)}
                  error={Boolean(errors?.shippingPostalCode)}
                  placeholder={`${merged.shippingPostalCodeLabel}*`}
                  aria-label={merged.shippingPostalCodeLabel}
                />
              </FloatingField>

              <FloatingField
                label={merged.shippingCountryLabel}
                hasValue={Boolean(values.shippingCountry)}
                error={errors?.shippingCountry}
              >
                <Dropdown
                  options={countryOptions}
                  value={values.shippingCountry || undefined}
                  placeholder={`${merged.shippingCountryLabel}*`}
                  onChange={(v) => onShippingChange('shippingCountry', v)}
                  aria-label={merged.shippingCountryLabel}
                  withSearch
                />
              </FloatingField>
            </SplitRow>
          </FormCard>
        </ShippingFormStack>
      )}
    </Wrapper>
  );
}
